import {
  Component,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  HostListener,
  Input,
  Output,
  EventEmitter
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ChatFireService } from '../../../gen2ai/chatfire.service';
import { Conversation } from '../../../models/chat.model';
import { SideMenusService } from '../side-menus.service';

interface ClarityMenuLink {
  label: string;
  route: string;
  exact?: boolean;
  labelHtml?: string;
  action?: 'logout' | 'newChat';
}

interface ClarityMenuSection {
  id: string;
  title: string;
  subtitle: string;
  iconSrc?: string;
  iconClass?: string;
  iconAlt?: string;
  links: ClarityMenuLink[];
}

@Component({
  selector: 'app-clarity-menu',
  templateUrl: './clarity-menu.component.html',
  styleUrls: ['./clarity-menu.component.scss']
})
export class ClarityMenuComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() fabCorner: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' = 'bottom-right';
  @Input() openStyle: 'fullscreen' | 'drawer-left' | 'drawer-right' = 'fullscreen';
  @Input() showReasoningDropdown = false;
  @Output() openChange = new EventEmitter<boolean>();

  @ViewChild('rail', { static: false }) railRef?: ElementRef<HTMLElement>;
  @ViewChild('panel', { static: false }) panelRef?: ElementRef<HTMLElement>;

  private readonly compactBreakpoint = 907;

  isMenuOpen = false;
  isPinnedOpen = false;
  activeSectionId = 'chat';
  isLoggedIn = false;
  isCompactMenu = false;

  private panelRafId: number | null = null;
  private uidSub?: Subscription;
  private routerSub?: Subscription;
  private mainMenuToggleSub?: Subscription;

  recentConversations: Conversation[] = [];
  loadingConversations = false;
  hoveredConversationId: string | null = null;
  editingTopicId: string | null = null;
  editingTopicValue = '';

  sections: ClarityMenuSection[] = [
    {
      id: 'chat',
      title: 'Chat Hubs',
      subtitle: 'X =  Responses ( AI )',
      iconSrc: 'assets/last-chat.svg',
      iconAlt: 'Chat',
      links: [
        {
          label: 'ExecutiveChat',
          labelHtml: '<i class="fa-solid fa-fire"></i><span class="fa-solid">&nbsp;ExecutiveChat&nbsp;<sup>6x</sup></span>',
          route: '/multi-ai/executive-chat'
        },
        {
          label: 'ClariChat',
          labelHtml: '<i class="fa-solid fa-home"></i><span class="fa-solid">&nbsp;Clari&nbsp;<sup>1x</sup>&nbsp;Chat</span>',
          route: '/ai/clarichat'
        },
        {
          label: 'Double Chat',
          labelHtml: '<i class="fa-solid fa-code-compare"></i><span class="fa-solid">&nbsp;Double&nbsp;<sup>2x</sup> Chat</span>',
          route: '/ai/doublechat'
        },
        {
          label: 'Model Garden',
          labelHtml: '<i class="fa-solid fa-seedling"></i><span class="fa-solid">&nbsp;Model Garden</span>',
          route: '/ai/model-garden'
        },
        {
          label: 'Search',
          labelHtml: '<i class="fa-solid fa-magnifying-glass"></i><span class="fa-solid">&nbsp;Search Chats</span>',
          route: '/library/search'
        }
      ]
    },
    {
      id: 'clarigen',
      title: 'Creative Visions',
      subtitle: 'Invision ~ Beautifully',
      iconSrc: 'assets/svg/media-library.svg',
      iconAlt: 'ClariGen',
      links: [
        { label: 'BotoxVision', labelHtml: '<i class="fas fa-spa"></i>&nbsp;<span class="fa-solid">BotoxVision</span>', route: '/library/botax-vison' },
        { label: 'BreastVision', labelHtml: '<i class="fas fa-shield-halved"></i>&nbsp;<span class="fa-solid">BreastVision</span>', route: '/library/breast-vison' },
        { label: 'IQ', labelHtml: '<i class="fa-solid fa-eye"></i>&nbsp;<span class="fa-solid">IQ</span>', route: '/multi-ai/iq' },
        { label: 'Personality', labelHtml: '<i class="fa-solid fa-user"></i>&nbsp;<span class="fa-solid">Personality</span>', route: '/multi-ai/personality' }
      ]
    },
    {
      id: 'create',
      title: 'Instruments',
      subtitle: 'Organize ~ Categorize',
      iconSrc: 'assets/svg/golden-guitar.svg',
      iconAlt: 'Create',
      links: [
        { label: 'Captions', labelHtml: '<i class="fa-solid fa-quote-left"></i>&nbsp;<span class="fa-solid">Funny Caps</span>', route: '/iq/copy' },
        { label: 'Note', route: '/notes', labelHtml: '<i class="fa-solid fa-file-lines"></i>&nbsp;<span class="fa-solid">Notes</span>' },
        { label: 'Calendar', labelHtml: '<i class="fa-solid fa-calendar"></i>&nbsp;<span class="fa-solid">Calendar</span> ', route: '/library/calendar' },
        { label: 'Media Library', labelHtml: '<i class="fa-solid fa-video"></i>&nbsp;<span class="fa-solid">Library AI</span>', route: '/iq/media-library' }
      ]
    },
    {
      id: 'account',
      title: 'Profile',
      subtitle: 'Preferences ~ Integrations',
      iconSrc: 'assets/svg/profile-rail.svg',
      iconAlt: 'Profile',
      links: [
        { label: 'IM', labelHtml: '<i class="fa fa-comment"></i>&nbsp;<span class="fa-solid">Friends Direct</span>', route: '/iq/friends' },
        { label: 'Kayak', labelHtml: '<i class="fa-solid fa-sailboat"></i>&nbsp;<span class="fa-solid">Apps</span>', route: '/iq/integrations/kayak' },
        { label: 'Log In', labelHtml: '<i class="fa-solid fa-right-from-bracket"></i>&nbsp;<span class="fa-solid">Log In</span>', route: '/auth/log-in' },
        { label: 'Logout', labelHtml: '<i class="fa-solid fa-right-from-bracket"></i>&nbsp;<span class="fa-solid">Logout</span>', action: 'logout', route: '/auth/log-in' }
      ]
    }
  ];

  constructor(
    private router: Router,
    private chatService: ChatFireService,
    private sideMenusService: SideMenusService
  ) {}

  ngOnInit(): void {
    this.updateViewportMode();

    this.uidSub = this.chatService.uid$.subscribe((uid) => {
      const loggedIn = !!uid && !uid.startsWith('anon-');
      this.isLoggedIn = loggedIn;

      if (loggedIn) {
        void this.loadRecentConversations();
      } else {
        this.recentConversations = [];
      }
    });

    this.routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const activeSection = this.sections.find((section) => this.isRouteInSection(section.id));
        if (activeSection) {
          this.activeSectionId = activeSection.id;
        }

        if (this.isLoggedIn) {
          setTimeout(() => {
            void this.loadRecentConversations();
          }, 500);
        }
      });

    this.mainMenuToggleSub = this.sideMenusService.toggleMainMenuSubject.subscribe((event) => {
      if (event === 'open') {
        this.openMenu();
      } else if (event === 'close') {
        this.closeMenu();
      } else {
        this.isMenuOpen ? this.closeMenu() : this.openMenu();
      }
    });

    const activeSection = this.sections.find((section) => this.isRouteInSection(section.id));
    if (activeSection) {
      this.activeSectionId = activeSection.id;
    }
  }

  ngAfterViewInit(): void {
    this.schedulePanelPosition();
  }

  ngOnDestroy(): void {
    this.uidSub?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.mainMenuToggleSub?.unsubscribe();

    if (this.panelRafId !== null) {
      cancelAnimationFrame(this.panelRafId);
      this.panelRafId = null;
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateViewportMode();
  }

  openMenu(): void {
    this.isMenuOpen = true;
    this.openChange.emit(true);
    this.sideMenusService.setMainMenuState(true);
    this.schedulePanelPosition();
  }

  closeMenu(): void {
    if (this.isPinnedOpen) {
      return;
    }
    this.isMenuOpen = false;
    this.openChange.emit(false);
    this.sideMenusService.setMainMenuState(false);
  }

  toggleMenu(): void {
    this.isMenuOpen ? this.closeMenu() : this.openMenu();
  }

  onIconHover(sectionId: string): void {
    this.activeSectionId = sectionId;
  }

  setActiveSection(sectionId: string): void {
    this.activeSectionId = sectionId;
    this.openMenu();
  }

  onIconClick(sectionId: string): void {
    if (this.activeSectionId === sectionId && this.isMenuOpen && !this.isPinnedOpen) {
      this.closeMenu();
      return;
    }

    this.activeSectionId = sectionId;
    this.openMenu();
  }

  togglePinnedOpen(event: Event): void {
    event.preventDefault();
    this.isPinnedOpen = !this.isPinnedOpen;
    if (this.isPinnedOpen) {
      this.openMenu();
    }
  }

  onPanelEnter(): void {
    if (!this.isMenuOpen) {
      this.openMenu();
    }
  }

  onPanelLeave(): void {
    if (!this.isPinnedOpen) {
      this.closeMenu();
    }
  }

  handleLinkClick(link: ClarityMenuLink, event?: Event): void {
    if (link.action === 'logout') {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      void this.logout();
    } else if (link.action === 'newChat') {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      void this.startNewChat();
    }

    if (!this.isPinnedOpen) {
      this.closeMenu();
    }
  }

  isLinkActive(link: ClarityMenuLink): boolean {
    const currentUrl = this.router.url.split('?')[0];
    if (link.exact) {
      return currentUrl === link.route;
    }

    return currentUrl.startsWith(link.route);
  }

  shouldShowLink(link: ClarityMenuLink): boolean {
    if (link.action === 'logout') {
      return this.isLoggedIn;
    }

    if (link.route === '/auth/log-in') {
      return !this.isLoggedIn;
    }

    return true;
  }

  trackBySection(index: number, section: ClarityMenuSection): string {
    return section.id;
  }

  trackByLink(index: number, link: ClarityMenuLink): string {
    return `${link.route}-${index}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isMenuOpen || this.isPinnedOpen) {
      return;
    }

    const target = event.target as Node;
    const clickedRail = this.railRef?.nativeElement.contains(target) ?? false;
    const clickedPanel = this.panelRef?.nativeElement.contains(target) ?? false;

    if (!clickedRail && !clickedPanel) {
      this.closeMenu();
    }
  }

  private updatePanelPosition(): void {
    if (this.railRef && this.panelRef) {
      const railRect = this.railRef.nativeElement.getBoundingClientRect();
      const panel = this.panelRef.nativeElement;
      panel.style.left = `${railRect.right + 12}px`;
      panel.style.top = `${railRect.top + 12}px`;
    }
  }

  private updateViewportMode(): void {
    this.isCompactMenu = this.getViewportWidth() <= this.compactBreakpoint;
  }

  private getViewportWidth(): number {
    return typeof window === 'undefined' ? 0 : window.innerWidth;
  }

  private schedulePanelPosition(): void {
    if (this.panelRafId !== null) {
      cancelAnimationFrame(this.panelRafId);
    }

    this.panelRafId = requestAnimationFrame(() => {
      this.panelRafId = null;
      this.updatePanelPosition();
    });
  }

  private isRouteInSection(sectionId: string): boolean {
    const currentUrl = this.router.url.split('?')[0];
    const section = this.sections.find((item) => item.id === sectionId);
    return section?.links.some((link) => currentUrl.startsWith(link.route)) ?? false;
  }

  get activeSection(): ClarityMenuSection {
    return this.sections.find((section) => section.id === this.activeSectionId) ?? this.sections[0];
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(this.chatService.logout());
    } catch (err) {
      console.error('[ClarityMenu] Logout failed', err);
    } finally {
      this.isLoggedIn = false;
      this.recentConversations = [];
      this.editingTopicId = null;
      this.editingTopicValue = '';
      this.closeMenu();
      await this.router.navigate(['/auth/log-in'], { queryParams: { loggedOut: 'true' } });
    }
  }

  async loadRecentConversations(): Promise<void> {
    if (!this.isLoggedIn) {
      this.recentConversations = [];
      return;
    }

    try {
      this.loadingConversations = true;
      const conversations = await firstValueFrom(this.chatService.getRecentConversations(6));
      this.recentConversations = this.filterDisplayableConversations(conversations);
    } catch (err) {
      console.error('[ClarityMenu] Error loading conversations', err);
      this.recentConversations = [];
    } finally {
      this.loadingConversations = false;
    }
  }

  async loadConversation(conversationId: string): Promise<void> {
    try {
      const conversation = await firstValueFrom(this.chatService.loadConversationById(conversationId));
      if (conversation) {
        await this.router.navigate(['/ai/clarichat'], { queryParams: { conversationId } });
        if (!this.isPinnedOpen) {
          this.closeMenu();
        }
      }
    } catch (error) {
      console.error('[ClarityMenu] Error loading conversation', error);
    }
  }

  async startNewChat(): Promise<void> {
    try {
      const newConversation = await firstValueFrom(this.chatService.createNewConversation());
      await this.router.navigate(['/ai/clarichat'], {
        queryParams: { conversationId: newConversation.id, newChat: 'true' }
      });
    } catch (err) {
      console.error('[ClarityMenu] Error starting new chat', err);
      await this.router.navigate(['/ai/clarichat']);
    }
  }

  truncateTopic(topic: string): string {
    if (!topic) return 'Classified Conversation';
    if (topic.length <= 25) return topic;
    return `${topic.substring(0, 25)}...`;
  }

  startEditingTopic(conversationId: string, currentTopic: string): void {
    this.editingTopicId = conversationId;
    this.editingTopicValue = currentTopic || '';
    setTimeout(() => {
      const input = document.querySelector('input.beta-conversation-topic-input') as HTMLInputElement | null;
      if (input) {
        input.focus();
        input.select();
      }
    }, 50);
  }

  async saveTopic(conversationId: string): Promise<void> {
    if (!this.editingTopicId || !this.editingTopicValue.trim()) {
      this.cancelEditingTopic();
      return;
    }

    try {
      const conversation = this.recentConversations.find((c) => c.id === conversationId);
      if (conversation) {
        conversation.topic = this.editingTopicValue.trim();
        await firstValueFrom(
          this.chatService.updateConversation({
            ...conversation,
            topic: this.editingTopicValue.trim()
          })
        );
        await this.loadRecentConversations();
      }
    } catch (err) {
      console.error('[ClarityMenu] Error saving topic', err);
    } finally {
      this.cancelEditingTopic();
    }
  }

  cancelEditingTopic(): void {
    this.editingTopicId = null;
    this.editingTopicValue = '';
  }

  trackByConversationId(index: number, conv: Conversation): string {
    return conv.id;
  }

  getSectionIconClass(): string {
    const iconMap: { [key: string]: string } = {
      chat: 'fa-solid fa-comment-dots',
      create: 'fa-solid fa-guitar',
      clarigen: 'fa-solid fa-brain',
      account: 'fa-solid fa-user'
    };
    return iconMap[this.activeSectionId] || 'fa-solid fa-comment-dots';
  }

  private filterDisplayableConversations(conversations: Conversation[]): Conversation[] {
    return conversations.filter((conversation) => {
      const messages = conversation.messages ?? [];
      if (messages.length < 2) {
        return false;
      }

      const hasUser = messages.some((message) => message.role === 'user');
      const hasAssistant = messages.some((message) => message.role === 'assistant');
      return hasUser && hasAssistant;
    });
  }
}